/**
 * @fileoverview MSAL (Microsoft Authentication Library) configuration for Azure B2C authentication.
 * This file contains the core configuration settings for MSAL authentication, including
 * auth settings, cache management, and system configurations.
 */

import { MsalGuardConfiguration, MsalInterceptorConfiguration } from "@azure/msal-angular";
import { BrowserCacheLocation, Configuration, LogLevel, InteractionType } from "@azure/msal-browser";
import { environment } from "src/environments/environment";
import { API_ACCESS_SCOPE, b2cPolicies, isIE, loginRequest } from 'src/configurations/b2c/b2c.config'

import { isDevMode } from "@angular/core";

/**
 * Core MSAL configuration object that defines authentication settings,
 * cache behavior, and system configurations for the application.
 *
 * @type {Configuration}
 */
export const msalConfig: Configuration = {
  auth: {
    clientId:                  b2cPolicies.clientId,                     // Azure B2C application client ID
    authority:                 b2cPolicies.authorities.signIn.authority, // B2C sign-in authority URL
    redirectUri:               b2cPolicies.redirectUri,                  // URI to redirect after authentication
    postLogoutRedirectUri:     b2cPolicies.redirectUri,                  // URI to redirect after logout
    knownAuthorities:          [b2cPolicies.authorityDomain],            // List of trusted authority domains
    navigateToLoginRequestUrl: true                                      // Whether to navigate to login page on auth failure
  },
  cache: {
    cacheLocation: BrowserCacheLocation.SessionStorage,                 // Store tokens in session storage
    storeAuthStateInCookie: isIE,                                       // Use cookies for IE compatibility
  },
  system: {
    tokenRenewalOffsetSeconds: b2cPolicies.tokenRenewalOffsetSeconds,   // Time before expiry to renew tokens
    loggerOptions: {
      loggerCallback: loggerCallback,                                   // Custom logging function
      logLevel: isDevMode() ? LogLevel.Verbose : LogLevel.Error,        // Verbose in dev, Error in prod
      piiLoggingEnabled: false                                          // Disable PII logging for security
    },
  },
};

/**
 * Custom logger callback function for MSAL that filters out PII (Personally Identifiable Information)
 * and formats log messages with appropriate log levels.
 *
 * @param {LogLevel} logLevel - The severity level of the log message
 * @param {string} message - The log message content
 * @param {boolean} containsPii - Whether the message contains PII
 */
export function loggerCallback(logLevel: LogLevel, message: string, containsPii: boolean): void {
  if (containsPii) return;
  const logMethod = logLevel === LogLevel.Error ? console.error : logLevel === LogLevel.Warning ? console.warn : console.log;
  logMethod(`[MSAL] ${LogLevel[logLevel]}: ${message}`);
}

/**
 * Configuration for MSAL Guard, which protects routes requiring authentication.
 * Uses redirect-based authentication flow. Requests API scope during login.
 *
 * @type {MsalGuardConfiguration}
 */
export const msalGuardConfig: MsalGuardConfiguration = {
  interactionType: InteractionType.Redirect,                            // Use redirect flow for authentication
  authRequest: loginRequest,                                           // Request with API access scope
};

/**
 * Configuration for MSAL Interceptor, which automatically adds authentication tokens
 * to HTTP requests for protected resources.
 *
 * @type {MsalInterceptorConfiguration}
 */
export const msalInterceptorConfig: MsalInterceptorConfiguration = {
  interactionType: InteractionType.Redirect,                            // Use redirect flow for token acquisition
  protectedResourceMap: new Map([[environment.base_url, [API_ACCESS_SCOPE]]]), // Map protected resources to required scopes
};

